/**
 * currencyUtils.js — Source unique de vérité pour les devises Colis2Ship.
 * Remplace les 6 copies locales de CCY_SYM / CURRENCIES_LIST.
 */

// stripe : devise de paiement acceptée par Stripe pour le compte (identique à STRIPE_SUPPORTED du backend,
// relevé auprès de Stripe le 2026-10-07). Seul le cedi ne peut pas être payé par carte.
export const SUPPORTED_CURRENCIES = [
  { code: 'EUR', symbol: '€',    name: 'Euro',               stripe: true  },
  { code: 'USD', symbol: '$',    name: 'Dollar US',          stripe: true  },
  { code: 'GBP', symbol: '£',    name: 'Livre sterling',     stripe: true  },
  { code: 'XOF', symbol: 'FCFA', name: 'Franc CFA Ouest',   stripe: true  },
  { code: 'XAF', symbol: 'FCFA', name: 'Franc CFA Central', stripe: true  },
  { code: 'CAD', symbol: 'CA$',  name: 'Dollar canadien',   stripe: true  },
  { code: 'CHF', symbol: 'CHF',  name: 'Franc suisse',      stripe: true  },
  { code: 'MAD', symbol: 'DH',   name: 'Dirham marocain',   stripe: true  },
  { code: 'NGN', symbol: '₦',    name: 'Naira nigérian',    stripe: true  },
  { code: 'GHS', symbol: '₵',    name: 'Cedi ghanéen',      stripe: false },
  { code: 'ZAR', symbol: 'R',    name: 'Rand sud-africain', stripe: true  },
  { code: 'AED', symbol: 'AED',  name: 'Dirham ÉAU',        stripe: true  },
  { code: 'CNY', symbol: '¥',    name: 'Yuan chinois',      stripe: true  },
];

export const CURRENCIES_LIST = SUPPORTED_CURRENCIES;

const _map   = Object.fromEntries(SUPPORTED_CURRENCIES.map(c => [c.code, c]));
const _codes = new Set(SUPPORTED_CURRENCIES.map(c => c.code));

export const CCY_SYM = Object.fromEntries(SUPPORTED_CURRENCIES.map(c => [c.code, c.symbol]));

export const ccySym       = (code) => _map[code]?.symbol ?? code ?? '€';
export const ccyName      = (code) => _map[code]?.name ?? code;
export const ccyStripeOk  = (code) => _map[code]?.stripe ?? false;
// Une devise non payable par carte (le cedi) sert à afficher un prix, jamais à conclure une proposition (D9)
export const NOT_CARD_PAYABLE_NOTE = "non payable par carte : sert à afficher un prix, pas à conclure";
export const hasCardPayable = (codes = []) => codes.some(ccyStripeOk);
export const isValidCurrency = (code) => _codes.has(code);

export function getCommonCurrencies(ccy1 = [], ccy2 = []) {
  return ccy1.filter(c => ccy2.includes(c));
}

export function areCurrenciesCompatible(ccy1 = [], ccy2 = []) {
  return getCommonCurrencies(ccy1, ccy2).length > 0;
}

export function pickCurrency(ccys1 = ['EUR'], ccys2 = ['EUR']) {
  const common = getCommonCurrencies(ccys1, ccys2);
  return common[0] ?? ccys2[0] ?? ccys1[0] ?? 'EUR';
}

export function normalizeCurrencies(item) {
  const ccys = Array.isArray(item?.currencies) && item.currencies.length > 0
    ? item.currencies
    : [item?.primary_currency || 'EUR'];
  return ccys;
}

const _ZERO_DEC = new Set(['XOF', 'XAF', 'NGN', 'GHS']);

/**
 * Formate un montant dans une devise donnée pour affichage en fr-FR.
 * Exemple : formatAmount(1234.5, 'EUR') → "1 234,5 €"
 *           formatAmount(60000, 'XOF') → "60 000 FCFA"
 */
export function formatAmount(amount, currency) {
  if (amount == null || !currency) return '';
  const sym = ccySym(currency);
  const decimals = _ZERO_DEC.has(currency) ? 0 : 2;
  const n = Number(amount).toLocaleString('fr-FR', {
    minimumFractionDigits: 0,
    maximumFractionDigits: decimals,
  });
  return `${n} ${sym}`;
}

/**
 * Date « AAAA-MM-JJ » au format français « JJ/MM/AAAA », lue telle quelle : `new Date('2026-10-16')` est minuit en
 * temps universel, et s'afficherait la veille sur un écran à l'ouest de Greenwich.
 */
export function frDate(iso) {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso || '');
  return m ? `${m[3]}/${m[2]}/${m[1]}` : '';
}

/**
 * Ce que l'écran dit de la date d'un taux de change (D7) : « taux du 16/10/2026 » (date de publication), « parité
 * fixe » (euro ↔ franc CFA), « taux de secours » (service de taux injoignable) ; rien pour une même devise.
 */
export function rateDateLabel(date, provider) {
  if (provider === 'fixed-parity') return 'parité fixe';
  if (provider === 'static-fallback') return 'taux de secours';
  return date ? `taux du ${frDate(date)}` : '';
}
